import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { APPROVAL_MODE_LABELS, ApprovalMode } from '@fleet/shared';
import { vehicleGroupsApi, type VehicleGroup } from '../../api/vehicle-groups';
import { AsyncBoundary } from '../../ui/AsyncBoundary';
import { Badge, ColorDot } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { DataTable } from '../../ui/DataTable';
import { ErrorNotice } from '../../ui/ErrorNotice';
import { VehicleGroupForm } from './VehicleGroupForm';

export function VehicleGroupsPage() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<VehicleGroup | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const query = useQuery({ queryKey: ['vehicle-groups'], queryFn: vehicleGroupsApi.list });

  const deactivate = useMutation({
    mutationFn: (id: string) => vehicleGroupsApi.deactivate(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['vehicle-groups'] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold">Fahrzeuggruppen</h2>
          <p className="text-sm text-slate-500">
            Bestimmen Freigabepflicht, Farbe und Berechtigungen der zugehörigen Fahrzeuge.
          </p>
        </div>
        <Button variant="primary" onClick={() => setIsCreating(true)}>
          Gruppe anlegen
        </Button>
      </div>

      {deactivate.isError && <ErrorNotice error={deactivate.error} />}

      <AsyncBoundary
        query={query}
        empty="Noch keine Fahrzeuggruppe angelegt. Ohne Gruppe lässt sich kein Fahrzeug erfassen."
      >
        {(groups) => (
          <DataTable
            rows={groups}
            rowKey={(group) => group.id}
            rowClassName={(group) => (group.isActive ? '' : 'text-slate-400')}
            columns={[
              {
                header: 'Name',
                cell: (group) => (
                  <div className="flex items-center gap-2">
                    <ColorDot color={group.colorHex} title={group.colorHex} />
                    <div>
                      <div className="font-medium">{group.name}</div>
                      {group.description && (
                        <div className="text-xs text-slate-500">{group.description}</div>
                      )}
                    </div>
                  </div>
                ),
              },
              {
                header: 'Freigabe',
                cell: (group) => (
                  <Badge tone={group.approvalMode === ApprovalMode.NONE ? 'neutral' : 'amber'}>
                    {APPROVAL_MODE_LABELS[group.approvalMode]}
                  </Badge>
                ),
              },
              { header: 'Fahrzeuge', align: 'right', cell: (group) => group.vehicleCount },
              {
                header: 'Status',
                cell: (group) =>
                  group.isActive ? (
                    <Badge tone="emerald">Aktiv</Badge>
                  ) : (
                    <Badge tone="neutral">Deaktiviert</Badge>
                  ),
              },
              {
                header: '',
                align: 'right',
                cell: (group) => (
                  <div className="flex justify-end gap-1">
                    <Link
                      to={`/admin/vehicle-groups/${group.id}/slack`}
                      className="rounded-md px-2.5 py-1 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900"
                    >
                      {group.hasSlackConfig ? 'Slack ✓' : 'Slack'}
                    </Link>
                    <Link
                      to={`/admin/vehicle-groups/${group.id}/permissions`}
                      className="rounded-md px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                    >
                      Berechtigungen
                    </Link>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(group)}>
                      Bearbeiten
                    </Button>
                    {group.isActive && (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={deactivate.isPending}
                        onClick={() => {
                          if (
                            confirm(
                              `„${group.name}" deaktivieren?\n\nDie Gruppe bleibt erhalten, weil Buchungen an ihr hängen. Ihre Fahrzeuge bleiben aktiv — die müssen einzeln stillgelegt werden.`,
                            )
                          ) {
                            deactivate.mutate(group.id);
                          }
                        }}
                      >
                        Deaktivieren
                      </Button>
                    )}
                  </div>
                ),
              },
            ]}
          />
        )}
      </AsyncBoundary>

      {isCreating && <VehicleGroupForm onClose={() => setIsCreating(false)} />}
      {editing && <VehicleGroupForm group={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}
